import Head from "next/head";
import { useEffect, useState, useCallback } from "react";
import { Spinner } from "react-bootstrap";
import styled, { createGlobalStyle } from "styled-components";
import { useRouter } from "next/router";
import WhatsAppButton from "../../../components/WhatsAppButton";
import BackTopButton from "../../../components/blog/buttons/backtop.button";
import { BlogButton } from "../../../components/blog/buttons/button";
import { PostCard } from "../../../components/blog/cards/post.card";
import {
	CardText,
	RecentPostCard,
} from "../../../components/blog/cards/recent.post.card";
import { Footer } from "../../../components/blog/footerBlog";
import { Header } from "../../../components/blog/header";
import { Paginator } from "../../../components/blog/paginator";
import { SearchBar } from "../../../components/blog/search";
import { device, size } from "../../../constants";
import { useTheme } from "../../../contexts/ThemeContext";
import { useWindowSize } from "../../../hooks/useWindowResize";
import { getLastPost, getPaginatedPosts } from "../../../services/CMSBlogService";
import { useTranslation } from "next-i18next";
import { getStaticPaths, getI18nProps } from "../../../lib/next-i18next/getStatic";
import { FaceLiftGlobalStyle } from "../../../styles/theme";

const BlogTitle = styled.h4`
  color: ${({ theme }) => theme.colors["neutral-07"]};
  font-weight: 500;
  font-size: 24px;
  line-height: 150%;
  margin-bottom: 16px;
  padding: 0 !important;
  padding-top: 16px !important;
  position: relative;
  text-align: center;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 32px;
    height: 4px;
    background: ${({ theme }) => theme.colors["branding-02"]};
    border-radius: 100px;
  }

  @media ${device.laptop} {
    text-align: left;
    margin-bottom: 24px;
    padding-top: 24px !important;
    
    &::before {
      width: 48px;
      left: 0;
      transform: none;
    }
  }

  :is([data-theme="dark"] &){
    color: ${({ theme }) => theme.colors["neutral-01"]};

    &::before {
      background: ${({ theme }) => theme.colors["vivid-02"]};
    }
  }
`;

const RecentPostWrapper = styled.div`
  background-color: ${({ theme }) => theme.colors["vz-background-01"]};

	> .container-blog {	
		@media ${device.laptop} {
			padding: 80px;
		}
	}

  :is([data-theme="dark"] &){
    background-color: ${({ theme }) => theme.colors["neutral-06"]};
  }
`;

const PostsWrapper = styled.div`
  a + a {
    margin-top: 40px;
  }
`;

export const CustomContainer = styled.div`
	position: relative;	
	
	:is([data-theme="dark"] &){
		background-color: ${({ theme }) => theme.colors["neutral-07"]};
	}
`;

function Index({ postsData }) {
	const { lastPost } = postsData;

	const router = useRouter();
	const locale = router?.query?.locale ?? 'pt';
	const { t } = useTranslation();

	const [loadMoreStep, setLoadMoreStep] = useState(2);
	const [loading, setLoading] = useState(false);

	const [_search, setSearch] = useState('');
	const [_pages, setPages] = useState(1);
	const [_posts, setPosts] = useState([]);
	const [_page, setPage] = useState(1);
	const [totalPosts, setTotalPosts] = useState(0);

	const { width } = useWindowSize();
	const { theme } = useTheme();

	const isLaptop = width >= size.laptop;

	const handleDuplicates = useCallback((posts) => {
		const duplicateIndex = posts.findIndex(
			(post) => post.title === lastPost.title,
		);

		if (duplicateIndex === -1) {
			return posts;
		}

		const postsCopy = [...posts];
		const duplicatePost = postsCopy[duplicateIndex];
		postsCopy.splice(duplicateIndex, 1);
		postsCopy.push(duplicatePost);

		return postsCopy;
	}, [lastPost]);

	const getPosts = async (page, search) => {
		const data = await getPaginatedPosts({
			search: search ?? _search ?? undefined,
			limit: postsData.limit,
			page: page ?? 1,
			locale
		});

		setTotalPosts(data?.total ?? 0);

		setPages(Math.ceil((data?.total ?? postsData.limit) / postsData.limit));

		if (data?.posts) {
			const postsWithoutDuplicates = handleDuplicates(data.posts)
			setPosts(postsWithoutDuplicates ?? []);
		} else {
			setPosts(data?.posts.filter(Boolean) ?? []);
		}
			
		setPage(page);
	};

	const handleSubmit = async (event) => {
		setLoading(true);
		event.preventDefault();
		setSearch(event.target.value);
		await getPosts(1, event.target.value);
		setLoading(false);
	};

	const loadMore = async () => {
		const morePosts = await getPaginatedPosts({
			search: _search,
			page: _page + 1,
			limit: postsData.limit,
			locale
		});

		if (morePosts?.posts?.length) {
			setTotalPosts(morePosts.total);
			setPosts((prev) => [...prev, ...morePosts.posts]);
			setLoadMoreStep((prev) => prev + 1);
			setPage((prev) => prev + 1);
		}
	};

	const onPrev = async () => {
		await getPosts(_page - 1);
	};

	const onNext = async () => {
		await getPosts(_page + 1);
	};

	const isDarkTheme = theme === "dark";

	useEffect(() => {
		const postsWithoutDuplicates = handleDuplicates(postsData.posts);
		setSearch(postsData.search ?? '');
		setPages(postsData.pages ?? 1);
		setPosts(postsWithoutDuplicates ?? []);
		setPage(postsData.page ?? 1);

		return () => {
			setPosts([]);
			setPage(1);
			setPages(1);
			setTotalPosts(0);
		}
	}, [postsData, handleDuplicates])

	return (
		<>
			<Head>
				{/* <!-- Primary Meta Tags --> */}
				<title>Verzel - Blog</title>
				<meta name="title" content="Verzel - Blog" />
				<meta
					name="description"
					content="Conteúdos sobre tecnologia indispensáveis para você!"
				/>
			</Head>

			<Header />

			<FaceLiftGlobalStyle />

			<WhatsAppButton />

			<RecentPostWrapper>
				<div className="container-blog">
					{lastPost && <RecentPostCard data={lastPost} />}
				</div>
			</RecentPostWrapper>

			<CustomContainer>
				<div className="container-blog">
					<BlogTitle className="px-3">{t("blog.latestPosts")}</BlogTitle>
					<SearchBar handleSubmit={handleSubmit} />
					{!_posts?.length && !loading && (
						<CardText className="text-center">
							{t("blog.noResults")}
						</CardText>
					)}

					{!_posts?.length && loading && (
						<div className="d-flex justify-content-center">
							<Spinner
								animation="border"
								role="status"
								variant={isDarkTheme ? "light" : "dark"}
							>
								<span className="visually-hidden">Loading...</span>
							</Spinner>
						</div>
					)}

					<PostsWrapper>
						{!!_posts.length && _posts?.map((post) => {
							return <PostCard data={post} key={post.slug} />;
						})}
					</PostsWrapper>

					{isLaptop && (
						<Paginator
							totalPages={_pages}
							page={_page ?? 1}
							onPrev={onPrev}
							onNext={onNext}
						/>
					)}

					{!isLaptop && totalPosts > _posts?.length && (
						<BlogButton
							outline={isDarkTheme}
							className="mt-5"
							variant="green"
							onClick={loadMore}
						>
							{t("blog.loadMore")}
						</BlogButton>
					)}
				</div>
				<BackTopButton />
			</CustomContainer>
			<Footer />
		</>
	);
}


export const getStaticProps = async (ctx) => {
	const i18nProps = await getI18nProps(ctx);
  const { query } = ctx;
	const locale = ctx.params?.locale ?? 'pt';
	const PAGINATION_LIMIT = 5;
	
	const _params = {
		limit: query?.limit ?? PAGINATION_LIMIT,
		search: query?.search ?? null,
		page: query?.page ?? 1,
		locale,
	};

	const lastPost = await getLastPost(locale);
	const paginatedPosts = await getPaginatedPosts(_params);
	
	return {
		props: {
			...i18nProps,
			postsData: {
				pages: Math.ceil(
					(paginatedPosts?.total ?? PAGINATION_LIMIT) / _params.limit,
				),
				page: query?.page ? Number.parseInt(_params.page) : null,
				posts: paginatedPosts?.posts ?? [],
				search: _params.search,
				limit: _params.limit,
				lastPost,
			},
		}
	};
}

export { getStaticPaths }

export default Index;